// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { CheckCircle2, Loader2, Save, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/app/components/page-layout';
import { UnsavedChangesGuard } from '@/app/components/unsaved-changes-guard';
import { useUnsavedChanges } from '@/app/hooks/useUnsavedChanges';
import { useEffect, useState, useCallback } from 'react';
import {
  useProfile,
  useUpdateProfile,
  usePolishProfileSummary,
  profileSchema,
  defaultPhotoSettings,
} from '@/app/features/profile';
import type { ProfileFormData, PhotoSettings } from '@/app/features/profile';

const emptyForm: ProfileFormData = {
  name: '',
  phone: '',
  address: '',
  professional_summary: '',
  linkedin_url: '',
  github_url: '',
  portfolio_url: '',
  output_language_mode: 'detected',
  custom_output_language: '',
  photo_url: '',
  photo_settings: { ...defaultPhotoSettings },
};

export function ProfessionalProfilePage() {
  const profile = useProfile();
  const updateProfile = useUpdateProfile();
  const polishSummary = usePolishProfileSummary();

  const [formData, setFormData] = useState<ProfileFormData>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof ProfileFormData, string>>
  >({});
  const [polishing, setPolishing] = useState(false);
  const [polishResult, setPolishResult] = useState<string | null>(null);
  const [includeExperiences, setIncludeExperiences] = useState(false);
  const [photoValid, setPhotoValid] = useState<boolean | null>(null); // null = not checked, true = OK, false = broken

  const detectedLanguageLabel =
    profile.data?.detected_cv_language_label || 'English';
  const effectiveOutputLanguage =
    formData.output_language_mode === 'english'
      ? 'English'
      : formData.output_language_mode === 'custom'
        ? (formData.custom_output_language ?? '').trim() || 'English'
        : detectedLanguageLabel;

  const unsaved = useUnsavedChanges(formData, { enabled: !!profile.data });

  // Populate form when profile data loads.
  useEffect(() => {
    if (profile.data) {
      const loaded: ProfileFormData = {
        name: profile.data.name ?? '',
        phone: profile.data.phone ?? '',
        address: profile.data.address ?? '',
        professional_summary: profile.data.professional_summary ?? '',
        linkedin_url: profile.data.linkedin_url ?? '',
        github_url: profile.data.github_url ?? '',
        portfolio_url: profile.data.portfolio_url ?? '',
        output_language_mode: profile.data.output_language_mode ?? 'detected',
        custom_output_language: profile.data.custom_output_language ?? '',
        photo_url: profile.data.photo_url ?? '',
        photo_settings: {
          ...defaultPhotoSettings,
          ...(profile.data.photo_settings as PhotoSettings | undefined),
        },
      };
      setFormData(loaded);
      unsaved.snapshot(loaded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unsaved.snapshot is stable; including unsaved would cause infinite resets
  }, [profile.data]);

  const handlePhotoSetting = useCallback(
    <K extends keyof PhotoSettings>(key: K, value: PhotoSettings[K]) => {
      setFormData((prev) => ({
        ...prev,
        photo_settings: { ...defaultPhotoSettings, ...prev.photo_settings, [key]: value },
      }));
    },
    [],
  );

  function handleChange(field: keyof ProfileFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = profileSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof ProfileFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof ProfileFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    updateProfile.mutate(formData, {
      onSuccess: () => {
        unsaved.snapshot(formData);
        toast.success('Profile updated successfully.');
      },
    });
  }

  if (profile.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (profile.error) {
    return (
      <div>
        <PageHeader title="Profile" />
        <div
          className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          Failed to load profile. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Profile"
        description="Your personal information, professional summary, and links. The summary is used as a starting point when generating tailored CVs."
      />

      {/* Server error */}
      {updateProfile.error && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {updateProfile.error.message}
        </div>
      )}

      {/* Polish error */}
      {polishSummary.error && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {polishSummary.error.message}
        </div>
      )}

      <form className="mt-6 space-y-8" onSubmit={handleSubmit} noValidate>
        {/* Personal Information */}
        <section>
          <h2 className="text-[16px] font-semibold text-foreground">
            Personal Information
          </h2>
          <p className="mt-0.5 text-[14px] text-muted-foreground">
            Name, phone, and address are encrypted at rest for your privacy.
          </p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="name"
                className="mb-1.5 block text-[14px] font-medium text-foreground"
              >
                Full name
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="Jane Doe"
                autoComplete="name"
                aria-invalid={!!fieldErrors.name}
                aria-describedby={
                  fieldErrors.name ? 'name-error' : undefined
                }
              />
              {fieldErrors.name && (
                <p
                  id="name-error"
                  className="mt-1 text-[13px] text-destructive"
                >
                  {fieldErrors.name}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-[14px] font-medium text-foreground"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={profile.data?.email ?? ''}
                readOnly
                className="block w-full rounded-md border border-input bg-muted px-3 py-2 text-[15px] text-muted-foreground cursor-not-allowed focus:outline-none"
              />
              <p className="mt-1 text-[13px] text-muted-foreground">
                Set during signup and cannot be changed.
              </p>
            </div>

            <div>
              <label
                htmlFor="phone"
                className="mb-1.5 block text-[14px] font-medium text-foreground"
              >
                Phone number
              </label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="+1 (555) 123-4567"
                autoComplete="tel"
                aria-invalid={!!fieldErrors.phone}
                aria-describedby={
                  fieldErrors.phone ? 'phone-error' : undefined
                }
              />
              {fieldErrors.phone && (
                <p
                  id="phone-error"
                  className="mt-1 text-[13px] text-destructive"
                >
                  {fieldErrors.phone}
                </p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="address"
                className="mb-1.5 block text-[14px] font-medium text-foreground"
              >
                Address
              </label>
              <input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="City, State, Country"
                autoComplete="street-address"
                aria-invalid={!!fieldErrors.address}
                aria-describedby={
                  fieldErrors.address ? 'address-error' : undefined
                }
              />
              {fieldErrors.address && (
                <p
                  id="address-error"
                  className="mt-1 text-[13px] text-destructive"
                >
                  {fieldErrors.address}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Profile Photo */}
        <section>
          <h2 className="text-[16px] font-semibold text-foreground">
            Profile Photo
          </h2>
          <p className="mt-0.5 text-[14px] text-muted-foreground">
            Provide a publicly accessible URL to your photo. It will appear on
            CV templates that support photos. The URL must be publicly
            accessible for PDF generation.
          </p>

          <div className="mt-4 flex gap-6">
            {/* Preview */}
            <div className="flex flex-col items-center gap-2">
              {formData.photo_url ? (
                <div
                  style={{
                    width:
                      (formData.photo_settings?.size ?? 'medium') === 'small'
                        ? 60
                        : (formData.photo_settings?.size ?? 'medium') === 'large'
                          ? 120
                          : 90,
                    height:
                      (formData.photo_settings?.size ?? 'medium') === 'small'
                        ? 60
                        : (formData.photo_settings?.size ?? 'medium') === 'large'
                          ? 120
                          : 90,
                    borderRadius:
                      (formData.photo_settings?.shape ?? 'circle') === 'circle'
                        ? '50%'
                        : (formData.photo_settings?.shape ?? 'circle') === 'rounded'
                          ? 8
                          : 0,
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                  className="border border-border bg-muted"
                >
                  {photoValid === false ? (
                    <div className="flex h-full w-full items-center justify-center bg-muted text-[11px] text-muted-foreground text-center px-1">
                      Photo not available
                    </div>
                  ) : (
                    <img
                      src={formData.photo_url}
                      alt="Profile preview"
                      onLoad={() => setPhotoValid(true)}
                      onError={() => setPhotoValid(false)}
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform:
                          (formData.photo_settings?.zoom ?? 1) > 1
                            ? `scale(${formData.photo_settings?.zoom ?? 1})`
                            : undefined,
                        objectPosition:
                          (formData.photo_settings?.offsetX ?? 0) !== 0 ||
                          (formData.photo_settings?.offsetY ?? 0) !== 0
                            ? `${50 + (formData.photo_settings?.offsetX ?? 0)}% ${50 + (formData.photo_settings?.offsetY ?? 0)}%`
                            : undefined,
                        filter:
                          (formData.photo_settings?.filter ?? 'none') === 'grayscale'
                            ? 'grayscale(100%)'
                            : (formData.photo_settings?.filter ?? 'none') === 'sepia'
                              ? 'sepia(80%)'
                              : (formData.photo_settings?.filter ?? 'none') === 'high-contrast'
                                ? 'contrast(130%) saturate(120%)'
                                : undefined,
                      }}
                    />
                  )}
                </div>
              ) : (
                <div
                  className="flex items-center justify-center border border-dashed border-border bg-muted text-[11px] text-muted-foreground"
                  style={{
                    width: 90,
                    height: 90,
                    borderRadius: '50%',
                  }}
                >
                  No photo
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex-1 space-y-3">
              <div>
                <label
                  htmlFor="photo-url"
                  className="mb-1.5 block text-[14px] font-medium text-foreground"
                >
                  Photo URL
                </label>
                <input
                  id="photo-url"
                  type="url"
                  value={formData.photo_url ?? ''}
                  onChange={(e) => {
                    handleChange('photo_url', e.target.value);
                    setPhotoValid(null);
                  }}
                  className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="https://example.com/photo.jpg"
                  autoComplete="url"
                />
              </div>

              {formData.photo_url && (
                <div className="space-y-3">
                  {/* Shape */}
                  <div>
                    <span className="mb-1 block text-[13px] font-medium text-foreground">
                      Shape
                    </span>
                    <div className="flex gap-1.5">
                      {(['circle', 'rounded', 'square'] as const).map((shape) => (
                        <button
                          key={shape}
                          type="button"
                          onClick={() => handlePhotoSetting('shape', shape)}
                          className={`rounded-md border px-3 py-1 text-[13px] capitalize transition-colors ${
                            (formData.photo_settings?.shape ?? 'circle') === shape
                              ? 'border-primary bg-primary/10 text-primary font-medium'
                              : 'border-input text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          {shape}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Size */}
                  <div>
                    <span className="mb-1 block text-[13px] font-medium text-foreground">
                      Size
                    </span>
                    <div className="flex gap-1.5">
                      {(['small', 'medium', 'large'] as const).map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => handlePhotoSetting('size', size)}
                          className={`rounded-md border px-3 py-1 text-[13px] capitalize transition-colors ${
                            (formData.photo_settings?.size ?? 'medium') === size
                              ? 'border-primary bg-primary/10 text-primary font-medium'
                              : 'border-input text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Zoom */}
                  <div>
                    <label
                      htmlFor="photo-zoom"
                      className="mb-1 block text-[13px] font-medium text-foreground"
                    >
                      Zoom ({((formData.photo_settings?.zoom ?? 1) * 100).toFixed(0)}%)
                    </label>
                    <input
                      id="photo-zoom"
                      type="range"
                      min="1"
                      max="2"
                      step="0.05"
                      value={formData.photo_settings?.zoom ?? 1}
                      onChange={(e) => handlePhotoSetting('zoom', parseFloat(e.target.value))}
                      className="w-full accent-primary"
                    />
                  </div>

                  {/* Offset X / Y */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor="photo-offset-x"
                        className="mb-1 block text-[13px] font-medium text-foreground"
                      >
                        Offset X ({formData.photo_settings?.offsetX ?? 0})
                      </label>
                      <input
                        id="photo-offset-x"
                        type="range"
                        min="-50"
                        max="50"
                        step="1"
                        value={formData.photo_settings?.offsetX ?? 0}
                        onChange={(e) =>
                          handlePhotoSetting('offsetX', parseInt(e.target.value, 10))
                        }
                        className="w-full accent-primary"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="photo-offset-y"
                        className="mb-1 block text-[13px] font-medium text-foreground"
                      >
                        Offset Y ({formData.photo_settings?.offsetY ?? 0})
                      </label>
                      <input
                        id="photo-offset-y"
                        type="range"
                        min="-50"
                        max="50"
                        step="1"
                        value={formData.photo_settings?.offsetY ?? 0}
                        onChange={(e) =>
                          handlePhotoSetting('offsetY', parseInt(e.target.value, 10))
                        }
                        className="w-full accent-primary"
                      />
                    </div>
                  </div>

                  {/* Filter */}
                  <div>
                    <label
                      htmlFor="photo-filter"
                      className="mb-1 block text-[13px] font-medium text-foreground"
                    >
                      Filter
                    </label>
                    <select
                      id="photo-filter"
                      value={formData.photo_settings?.filter ?? 'none'}
                      onChange={(e) =>
                        handlePhotoSetting(
                          'filter',
                          e.target.value as PhotoSettings['filter'],
                        )
                      }
                      className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="none">None</option>
                      <option value="grayscale">Grayscale</option>
                      <option value="sepia">Sepia</option>
                      <option value="high-contrast">High Contrast</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Professional Summary */}
        <section>
          <div className="flex items-center justify-between">
            <h2 className="text-[16px] font-semibold text-foreground">
              Summary
            </h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={includeExperiences}
                  onChange={(e) => setIncludeExperiences(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-input accent-primary"
                />
                Include experiences
              </label>
              <button
                type="button"
                disabled={
                  (!(formData.professional_summary ?? '').trim() && !includeExperiences) ||
                  polishing
                }
                onClick={() => {
                  setPolishing(true);
                  setPolishResult(null);
                  polishSummary.mutate(
                    {
                      summary: formData.professional_summary,
                      include_experiences: includeExperiences,
                    },
                    {
                      onSuccess: (result) => {
                        setPolishResult(
                          (result as { polished: string }).polished,
                        );
                        setPolishing(false);
                      },
                      onError: () => {
                        setPolishing(false);
                      },
                    },
                  );
                }}
                className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-[12px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
                title="AI polish this summary"
              >
                {polishing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                AI Polish
              </button>
            </div>
          </div>
          <p className="mt-0.5 text-[14px] text-muted-foreground">
            A brief overview of your professional background. It appears on every
            generated CV exactly as written here; use AI Polish to refine it.
          </p>

          <div className="mt-4">
            <label htmlFor="professional-summary" className="sr-only">
              Professional summary
            </label>
            <textarea
              id="professional-summary"
              value={formData.professional_summary}
              onChange={(e) =>
                handleChange('professional_summary', e.target.value)
              }
              rows={5}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Technical professional with over 10 years of experience in leading complex Cloud and on-premises projects..."
              aria-invalid={!!fieldErrors.professional_summary}
              aria-describedby={
                fieldErrors.professional_summary
                  ? 'summary-error'
                  : 'summary-hint'
              }
            />
            {fieldErrors.professional_summary && (
              <p
                id="summary-error"
                className="mt-1 text-[13px] text-destructive"
              >
                {fieldErrors.professional_summary}
              </p>
            )}
            <p
              id="summary-hint"
              className="mt-1.5 text-[13px] text-muted-foreground"
            >
              {(formData.professional_summary ?? '').length} / 5,000 characters
            </p>

            {/* Inline AI polish suggestion (visible when loading or has result) */}
            {(polishResult || polishing) && (
              <div className="mt-2 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                <div className="flex items-center gap-1.5">
                  {polishing ? (
                    <Loader2 className="h-3 w-3 animate-spin text-ai" />
                  ) : (
                    <Sparkles className="h-3 w-3 shrink-0 text-ai" />
                  )}
                  <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
                    {polishing ? 'Polishing...' : 'AI suggestion'}
                  </span>
                </div>
                {polishResult && (
                  <>
                    <p className="mt-1 whitespace-pre-wrap text-[14px] leading-relaxed text-foreground">
                      {polishResult}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          handleChange('professional_summary', polishResult);
                          setPolishResult(null);
                        }}
                        className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90"
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Use this
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPolishing(true);
                          setPolishResult(null);
                          polishSummary.mutate(
                            {
                              summary: formData.professional_summary,
                              include_experiences: includeExperiences,
                            },
                            {
                              onSuccess: (result) => {
                                setPolishResult(
                                  (result as { polished: string }).polished,
                                );
                                setPolishing(false);
                              },
                              onError: () => {
                                setPolishing(false);
                                setPolishResult(null);
                              },
                            },
                          );
                        }}
                        disabled={polishing}
                        className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                      >
                        <Sparkles className="h-3 w-3 text-ai" />
                        Regenerate
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPolishResult(null);
                          setPolishing(false);
                        }}
                        className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        Dismiss
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Professional Links */}
        <section>
          <h2 className="text-[16px] font-semibold text-foreground">Links</h2>
          <p className="mt-0.5 text-[14px] text-muted-foreground">
            Professional profile URLs. Displayed as plain text on generated CVs.
          </p>

          <div className="mt-4 space-y-4">
            <div>
              <label
                htmlFor="linkedin-url"
                className="mb-1.5 block text-[14px] font-medium text-foreground"
              >
                LinkedIn
              </label>
              <input
                id="linkedin-url"
                type="url"
                value={formData.linkedin_url}
                onChange={(e) => handleChange('linkedin_url', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="https://linkedin.com/in/janedoe"
                autoComplete="url"
                aria-invalid={!!fieldErrors.linkedin_url}
                aria-describedby={
                  fieldErrors.linkedin_url ? 'linkedin-error' : undefined
                }
              />
              {fieldErrors.linkedin_url && (
                <p
                  id="linkedin-error"
                  className="mt-1 text-[13px] text-destructive"
                >
                  {fieldErrors.linkedin_url}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="github-url"
                className="mb-1.5 block text-[14px] font-medium text-foreground"
              >
                GitHub
              </label>
              <input
                id="github-url"
                type="url"
                value={formData.github_url}
                onChange={(e) => handleChange('github_url', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="https://github.com/janedoe"
                autoComplete="url"
                aria-invalid={!!fieldErrors.github_url}
                aria-describedby={
                  fieldErrors.github_url ? 'github-error' : undefined
                }
              />
              {fieldErrors.github_url && (
                <p
                  id="github-error"
                  className="mt-1 text-[13px] text-destructive"
                >
                  {fieldErrors.github_url}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="portfolio-url"
                className="mb-1.5 block text-[14px] font-medium text-foreground"
              >
                Portfolio / Website
              </label>
              <input
                id="portfolio-url"
                type="url"
                value={formData.portfolio_url}
                onChange={(e) => handleChange('portfolio_url', e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="https://janedoe.dev"
                autoComplete="url"
                aria-invalid={!!fieldErrors.portfolio_url}
                aria-describedby={
                  fieldErrors.portfolio_url ? 'portfolio-error' : undefined
                }
              />
              {fieldErrors.portfolio_url && (
                <p
                  id="portfolio-error"
                  className="mt-1 text-[13px] text-destructive"
                >
                  {fieldErrors.portfolio_url}
                </p>
              )}
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-[15px] font-semibold text-foreground">CV Language</h2>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            AI output language for polishing and recommendations.
          </p>

          <div className="mt-3 rounded-md border border-border bg-background px-3 py-2 text-[13px]">
            <span className="text-muted-foreground">Detected:</span>{' '}
            <span className="font-medium text-foreground">
              {profile.data?.detected_cv_language_label || 'Not detected yet'}
            </span>
          </div>

          <div className="mt-3 space-y-2.5">
            <label className="flex items-center gap-2 text-[14px] text-foreground">
              <input
                type="radio"
                name="output-language-mode"
                checked={formData.output_language_mode === 'detected'}
                onChange={() => handleChange('output_language_mode', 'detected')}
                className="h-4 w-4 accent-primary"
              />
              Use detected language
            </label>

            <label className="flex items-center gap-2 text-[14px] text-foreground">
              <input
                type="radio"
                name="output-language-mode"
                checked={formData.output_language_mode === 'english'}
                onChange={() => handleChange('output_language_mode', 'english')}
                className="h-4 w-4 accent-primary"
              />
              English
            </label>

            <label className="flex items-center gap-2 text-[14px] text-foreground">
              <input
                type="radio"
                name="output-language-mode"
                checked={formData.output_language_mode === 'custom'}
                onChange={() => handleChange('output_language_mode', 'custom')}
                className="h-4 w-4 accent-primary"
              />
              Custom
            </label>

            {formData.output_language_mode === 'custom' && (
              <div>
                <label
                  htmlFor="custom-output-language"
                  className="mb-1.5 block text-[14px] font-medium text-foreground"
                >
                  Custom language
                </label>
                <input
                  id="custom-output-language"
                  type="text"
                  value={formData.custom_output_language ?? ''}
                  onChange={(e) =>
                    handleChange('custom_output_language', e.target.value)
                  }
                  className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Portuguese"
                />
              </div>
            )}

            <p className="text-[13px] text-muted-foreground">
              AI will generate output in{' '}
              <span className="font-medium text-foreground">
                {effectiveOutputLanguage}
              </span>
              .
            </p>
          </div>
        </section>

        {/* Submit */}
        <div className="flex items-center gap-3 border-t border-border pt-6">
           <button
            type="submit"
            disabled={updateProfile.isPending || !unsaved.hasChanges}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {updateProfile.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save changes
          </button>
        </div>
      </form>

      <UnsavedChangesGuard {...unsaved.dialogProps} />
    </div>
  );
}
